export class Loadable<T> {
    isBeingLoaded?: boolean;
    isLoaded?: boolean;
    loadingError?: AsyncError;
    obj?: T;

    static empty(): Loadable<any> {
        return EmptyLoadable;
    }

    static startLoading(): Loadable<any> {
        return { isBeingLoaded: true };
    }

    static finishLoading<T>(props: LoadableProps<T>): Loadable<T> {
        if (props.obj !== undefined) {
            return loadingSuccess(props.obj);
        } else if (props.loadingError) {
            return loadingFailed(props.loadingError);
        }
        return Loadable.empty();
    }

    static loadingError<T>(error: unknown): Loadable<T> {
        return Loadable.finishLoading<T>({
            loadingError: {
                message: JSON.stringify(error)
            }
        });
    }

    static loadingErrorWithCustomId<T>(customId: number, error?: unknown): Loadable<T> {
        return Loadable.finishLoading<T>({
            loadingError: {
                ...((error === undefined) ? {} : { message: JSON.stringify(error) }),
                customId,
            }
        });
    }

    static isError(loadable: Loadable<unknown>): boolean {
        return loadable.loadingError !== undefined;
    }

    static isFinished(loadable: Loadable<unknown>): boolean {
        return !loadable.isBeingLoaded;
    }
}

export function LoadableTransform<T, R>(t: Loadable<T>, fn: (t: T) => R): Loadable<R> {
    const transformedObj = t.obj === undefined
        ? undefined
        : fn(t.obj);
    return { ...t, obj: transformedObj };
}

export function TransformLoadables<A, B, R>(a: Loadable<A>, b: Loadable<B>, fn: (a: A, b: B) => R): Loadable<R> {
    const loadables = [a, b];
    if (loadables.some(l => l.isBeingLoaded)) return { isBeingLoaded: true };
    const errors = loadables.filter(l => l.loadingError !== undefined);
    if (errors.length !== 0) {
        return { loadingError: errors[0].loadingError };
    }
    if (a.obj === undefined || b.obj === undefined) {
        return { obj: undefined };
    }
    return { obj: fn(a.obj, b.obj) };
}

export interface LoadableProps<T> {
    obj?: T,
    loadingError?: AsyncError,
}

export const EmptyLoadable = {};

export const LoadableInProgress = { isBeingLoaded: true };

export function LoadableOf<T>(t: T): Loadable<T> {
    return { isLoaded: true, isBeingLoaded: false, obj: t };
}

function loadingSuccess<T>(t: T): Loadable<T> {
    return LoadableOf(t);
}

export interface AsyncError {
    customId?: number;
    message?: string;
}

function loadingFailed(loadingError: AsyncError): Loadable<any> {
    return { isLoaded: false, isBeingLoaded: false, loadingError };
}

export interface Saveable<T> {
    isBeingSaved?: boolean;
    isSaved?: boolean;
    obj?: T;
}

export const EmptySaveable = {};

export function SaveableOf<T>(t: T): Saveable<T> {
    return { isSaved: false, isBeingSaved: true, obj: t };
}

export function startSaving<T>(t: Saveable<T>): Saveable<T> {
    return {
        ...t,
        isBeingSaved: true,
    }
}